import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Car, MapPin, Plane, Globe, Shield, Clock, Users, Star, CheckCircle, Phone, Calculator } from 'lucide-react';

const Home = () => {
  const services = [
    {
      icon: Plane,
      title: 'Airport Transfer',
      description: 'Reliable pickup and drop services to/from Dehradun Airport'
    },
    {
      icon: Globe,
      title: 'Outstation Trips',
      description: 'Comfortable long-distance travel across India'
    },
    {
      icon: MapPin,
      title: 'Local Rides',
      description: 'Convenient local taxi services within Dehradun'
    },
    {
      icon: Car,
      title: 'Tour Packages',
      description: 'Curated tour packages for Uttarakhand destinations'
    }
  ];

  const features = [
    { icon: Shield, text: 'Safe & Secure' },
    { icon: Clock, text: '24/7 Available' },
    { icon: Users, text: 'Professional Drivers' },
    { icon: Star, text: 'Top Rated Service' }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="gradient-hero min-h-[90vh] flex items-center pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full border border-blue-200">
                <Star className="w-4 h-4 text-orange-500 fill-orange-500" />
                <span className="text-sm font-medium text-slate-700">Trusted by 10,000+ Happy Customers</span>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold text-slate-900 leading-tight tracking-tight">
                Reliable & Affordable <span className="text-blue-600">Cab Service</span> from Dehradun
              </h1>

              <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
                Experience comfortable and safe travel with Explore Destination Cab. Your journey, our priority.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/booking"
                  data-testid="hero-book-now-btn"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-orange-500/30 hover:-translate-y-0.5 uppercase tracking-wide text-center"
                >
                  Book Now
                </Link>
                <Link
                  to="/calculator"
                  data-testid="hero-calculate-fare-btn"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-8 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-blue-500/30 hover:-translate-y-0.5 text-center inline-flex items-center justify-center gap-2"
                >
                  <Calculator className="w-5 h-5" />
                  <span>Calculate Fare</span>
                </Link>
                <a
                  href="tel:+916396867682"
                  data-testid="hero-call-now-btn"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-medium px-8 py-4 rounded-full transition-all duration-300 text-center inline-flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  <span>Call Now</span>
                </a>
              </div>

              {/* Quick Features */}
              <div className="flex flex-wrap gap-6 pt-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <feature.icon className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{feature.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right Image/Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1642945543180-849ec3b93cef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGZhbWlseSUyMHRvdXJpc3RzJTIwbW91bnRhaW5zfGVufDB8fHx8MTc2OTMzNDI0MXww&ixlib=rb-4.1.0&q=85"
                  alt="Happy tourists enjoying Dehradun mountains"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent" />
                
                {/* Floating Quick Booking Card */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md p-6 rounded-2xl shadow-xl border border-white/20">
                  <h3 className="font-bold text-lg text-slate-900 mb-3">Quick Booking</h3>
                  <Link
                    to="/booking"
                    data-testid="hero-quick-booking-btn"
                    className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors duration-300 text-center"
                  >
                    Get Started →
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm text-orange-500 font-semibold uppercase tracking-wider mb-4">Our Services</p>
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 tracking-tight">
              Complete Travel Solutions
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`service-card-${index}`}
                className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:border-blue-100 transition-all duration-300 group gradient-card-hover"
              >
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl inline-block mb-6 group-hover:scale-110 transition-transform duration-300">
                  <service.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-slate-50" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.unsplash.com/photo-1727309842601-19c2d9e42a67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxzZWRhbiUyMGNhciUyMHNpZGUlMjB2aWV3JTIwd2hpdGUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc2OTMzNDIzOHww&ixlib=rb-4.1.0&q=85"
                alt="Premium Sedan Taxi"
                className="w-full h-[400px] object-cover rounded-3xl shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <p className="text-sm text-orange-500 font-semibold uppercase tracking-wider">Why Choose Us</p>
              <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 tracking-tight leading-tight">
                Your Trusted Travel Partner in Dehradun
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                With years of experience and thousands of satisfied customers, we provide reliable, safe, and affordable cab services across Uttarakhand and beyond.
              </p>

              <div className="space-y-4 pt-4">
                {[
                  'Well-maintained fleet of vehicles',
                  'Experienced & verified drivers',
                  'Transparent pricing with no hidden charges',
                  '24/7 customer support',
                  'Safe and comfortable journey',
                  'Quick and easy booking process'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="bg-green-100 p-1 rounded-full mt-0.5">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-slate-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/about"
                data-testid="learn-more-btn"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium px-8 py-3 rounded-full transition-all duration-300 shadow-lg hover:shadow-blue-500/30 hover:-translate-y-0.5 mt-6"
              >
                Learn More About Us
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-blue-700 text-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
              Ready to Start Your Journey?
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
              Book your cab now and experience comfortable, safe, and affordable travel with us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <a
                href="tel:+916396867682"
                data-testid="cta-call-btn"
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-10 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-orange-500/30 hover:-translate-y-0.5 uppercase tracking-wide inline-flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                <span>Call: +91-6396867682</span>
              </a>
              <Link
                to="/contact"
                data-testid="cta-contact-btn"
                className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-10 py-4 rounded-full transition-all duration-300 shadow-lg hover:-translate-y-0.5"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
